import { useState, useRef } from "react";
import type { Scenario, PlacedForce, ForceType, ForceDirection } from "@/lib/types";
import { SceneRenderer } from "./SceneRenderer";
import { ForceArrow, FORCE_COLORS, FORCE_LABELS } from "./ForceArrow";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const CANVAS_W = 420;
const CANVAS_H = 340;

const FORCE_TYPES: { type: ForceType; label: string }[] = [
	{ type: "gravity", label: "Gravity (Fg)" },
	{ type: "normal", label: "Normal (FN)" },
	{ type: "friction", label: "Friction (Ff)" },
	{ type: "tension", label: "Tension (FT)" },
	{ type: "applied", label: "Applied (Fapp)" },
	{ type: "air-resistance", label: "Air Resist. (Fair)" },
];

const DIRECTIONS: { dir: ForceDirection; label: string; icon: string }[] = [
	{ dir: "up", label: "Up", icon: "\u2191" },
	{ dir: "down", label: "Down", icon: "\u2193" },
	{ dir: "left", label: "Left", icon: "\u2190" },
	{ dir: "right", label: "Right", icon: "\u2192" },
	{ dir: "up-left", label: "Up-Left", icon: "\u2196" },
	{ dir: "up-right", label: "Up-Right", icon: "\u2197" },
	{ dir: "down-left", label: "Down-Left", icon: "\u2199" },
	{ dir: "down-right", label: "Down-Right", icon: "\u2198" },
];

interface FBDCanvasProps {
	scenario: Scenario;
	onCheck: (placedForces: PlacedForce[], correct: boolean) => void;
	showResult: null | "correct" | "incorrect";
	showAnswer: boolean;
}

export function FBDCanvas({ scenario, onCheck, showResult, showAnswer }: FBDCanvasProps) {
	const [placedForces, setPlacedForces] = useState<PlacedForce[]>([]);
	const [selectedType, setSelectedType] = useState<ForceType | null>(null);
	const [selectedDir, setSelectedDir] = useState<ForceDirection | null>(null);
	const svgRef = useRef<SVGSVGElement>(null);

	const addForce = () => {
		if (!selectedType || !selectedDir) return;
		// Prevent duplicates of same type+direction
		const exists = placedForces.some((f) => f.type === selectedType && f.direction === selectedDir);
		if (exists) return;
		setPlacedForces([...placedForces, { type: selectedType, direction: selectedDir }]);
		setSelectedType(null);
		setSelectedDir(null);
	};

	const removeForce = (index: number) => {
		if (showAnswer) return;
		setPlacedForces(placedForces.filter((_, i) => i !== index));
	};

	const clearAll = () => {
		setPlacedForces([]);
		setSelectedType(null);
		setSelectedDir(null);
	};

	const handleCheck = () => {
		// Check if placed forces match correct forces
		const correct = scenario.correctForces;
		const isCorrect =
			placedForces.length === correct.length &&
			correct.every((cf) => placedForces.some((pf) => pf.type === cf.type && pf.direction === cf.direction)) &&
			placedForces.every((pf) => correct.some((cf) => cf.type === pf.type && cf.direction === pf.direction));
		onCheck(placedForces, isCorrect);
	};

	const cx = CANVAS_W / 2;
	const cy = CANVAS_H / 2;

	// For ramp scenario, adjust object center for arrows
	const getObjectCenter = () => {
		if (scenario.environment === "ramp") {
			const rad = ((scenario.rampAngle ?? 30) * Math.PI) / 180;
			const rampLen = CANVAS_W * 0.7;
			const rampH = rampLen * Math.sin(rad);
			const rampW = rampLen * Math.cos(rad);
			const baseY = CANVAS_H * 0.75;
			const rampStartX = (CANVAS_W - rampW) / 2;
			const rampEndX = rampStartX + rampW;
			return { x: (rampStartX + rampEndX) / 2, y: baseY - rampH / 2 };
		}
		if (scenario.environment === "elevator") {
			const eH = CANVAS_H * 0.7;
			const eY = cy - eH / 2 + 10;
			return { x: cx, y: eY + eH - 65 };
		}
		if (scenario.environment === "hanging") {
			return { x: cx, y: cy + 10 };
		}
		return { x: cx, y: cy };
	};

	const objCenter = getObjectCenter();

	const forcesToRender = showAnswer ? scenario.correctForces.map((f) => ({ type: f.type, direction: f.direction })) : placedForces;

	return (
		<div className="space-y-4">
			{/* Canvas */}
			<div className="border rounded-lg bg-card overflow-hidden">
				<svg
					ref={svgRef}
					width={CANVAS_W}
					height={CANVAS_H}
					viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
					className="w-full h-auto"
					style={{ maxWidth: CANVAS_W }}
				>
					{/* Background grid */}
					<defs>
						<pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
							<path d="M 20 0 L 0 0 0 20" fill="none" stroke="hsl(var(--border))" strokeWidth={0.5} />
						</pattern>
					</defs>
					<rect width={CANVAS_W} height={CANVAS_H} fill="url(#grid)" />

					{/* Scene */}
					<SceneRenderer scenario={scenario} width={CANVAS_W} height={CANVAS_H} />

					{/* Dot at object center */}
					<circle cx={objCenter.x} cy={objCenter.y} r={3} fill="hsl(var(--foreground))" />

					{/* Force arrows */}
					{forcesToRender.map((f, i) => {
						const correctForce = scenario.correctForces.find((cf) => cf.type === f.type);
						const mag = correctForce?.magnitude ?? "medium";
						return (
							<ForceArrow
								key={`${f.type}-${f.direction}-${i}`}
								cx={objCenter.x}
								cy={objCenter.y}
								type={f.type}
								direction={f.direction}
								magnitude={mag}
								onRemove={showAnswer ? undefined : () => removeForce(i)}
							/>
						);
					})}
				</svg>
			</div>

			{/* Force palette */}
			{!showAnswer && (
				<div className="space-y-3">
					{/* Force type selection */}
					<div>
						<p className="text-xs font-medium text-muted-foreground mb-1.5">1. Select force type:</p>
						<div className="flex flex-wrap gap-1.5">
							{FORCE_TYPES.map((ft) => (
								<button
									key={ft.type}
									onClick={() => setSelectedType(ft.type)}
									className={cn(
										"px-2.5 py-1 rounded-md text-xs font-medium border transition-all",
										selectedType === ft.type
											? "ring-2 ring-ring border-transparent text-white"
											: "border-border hover:border-ring text-foreground"
									)}
									style={selectedType === ft.type ? { backgroundColor: FORCE_COLORS[ft.type] } : undefined}
								>
									{ft.label}
								</button>
							))}
						</div>
					</div>

					{/* Direction selection */}
					<div>
						<p className="text-xs font-medium text-muted-foreground mb-1.5">2. Select direction:</p>
						<div className="flex flex-wrap gap-1.5">
							{DIRECTIONS.map((d) => (
								<button
									key={d.dir}
									onClick={() => setSelectedDir(d.dir)}
									className={cn(
										"px-2.5 py-1 rounded-md text-xs font-medium border transition-all",
										selectedDir === d.dir
											? "bg-primary text-primary-foreground border-transparent"
											: "border-border hover:border-ring text-foreground"
									)}
								>
									{d.icon} {d.label}
								</button>
							))}
						</div>
					</div>

					{/* Add button */}
					<div className="flex gap-2">
						<Button
							size="sm"
							onClick={addForce}
							disabled={!selectedType || !selectedDir}
						>
							Add Force
						</Button>
						<Button size="sm" variant="outline" onClick={clearAll} disabled={placedForces.length === 0}>
							Clear All
						</Button>
					</div>

					{/* Placed forces */}
					{placedForces.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{placedForces.map((f, i) => (
								<Badge
									key={i}
									variant="secondary"
									className="cursor-pointer hover:bg-destructive/20 transition-colors gap-1"
									onClick={() => removeForce(i)}
								>
									<span
										className="inline-block w-2 h-2 rounded-full"
										style={{ backgroundColor: FORCE_COLORS[f.type] }}
									/>
									{FORCE_LABELS[f.type]} {DIRECTIONS.find((d) => d.dir === f.direction)?.icon}
									<span className="text-muted-foreground ml-0.5">&times;</span>
								</Badge>
							))}
						</div>
					)}

					{/* Check answer */}
					<Button
						onClick={handleCheck}
						disabled={placedForces.length === 0}
						className="w-full"
						variant={showResult === "correct" ? "default" : showResult === "incorrect" ? "destructive" : "default"}
					>
						{showResult === "correct"
							? "Correct!"
							: showResult === "incorrect"
								? "Try Again"
								: "Check My Diagram"}
					</Button>
				</div>
			)}
		</div>
	);
}
